(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports, require('@jsplumbtoolkit/browser-ui'), require('vue')) :
    typeof define === 'function' && define.amd ? define(['exports', '@jsplumbtoolkit/browser-ui', 'vue'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.jsPlumbToolkitVue3 = {}, global.jsPlumbToolkit, global.vue));
})(this, (function (exports, browserUi, vue) { 'use strict';

    /**
     * Toolkit instance for use with Vue3 integration.
     * @internal
     */
    class BrowserUIVue3 extends browserUi.BrowserUIBase {
      render(container, options) {
        browserUi.log("render called directly on BrowserUIVue3 class: should not happen. Surface component should use internal render.");
        return null;
      }
    }

    /**
     * Create a new instance of the Toolkit to use with the Vue 3 integration.
     * @param options - Options for the Toolkit instance.
     * @internal
     */
    function newInstance(options) {
      options = options || {};
      return new BrowserUIVue3(options);
    }

    let _surfaces = {},
      _workQueues = {},
      _handlers = {
        "miniview": (surface, params) => {
          let miniview = surface.addPlugin({
            type: browserUi.MiniviewPlugin.type,
            options: params
          });
          surface.toolkitInstance.bind(browserUi.EVENT_DATA_LOAD_END, () => {
            miniview.invalidate();
          });
          surface.toolkitInstance.bind(browserUi.EVENT_NODE_ADDED, params => {
            miniview.invalidate(params.node.id);
          });
          surface.toolkitInstance.bind(browserUi.EVENT_GROUP_ADDED, params => {
            miniview.invalidate(params.node.id);
          });
        }
      },
      _addToWorkQueue = (surfaceId, params, handler) => {
        let s = _surfaces[surfaceId];
        if (s) {
          handler(s, params);
        } else {
          _workQueues[surfaceId] = _workQueues[surfaceId] || [];
          _workQueues[surfaceId].push([params, handler]);
        }
      };
    /**
     * @internal
     * @param id
     * @param toolkitParams
     */
    function addToolkit(id, toolkitParams) {
      id = id || browserUi.uuid();
      let toolkit = new BrowserUIVue3(toolkitParams || {});
      toolkit._vueId = id;
      return toolkit;
    }

    /**
     * @internal
     * @param toolkitComponent
     * @param toolkit
     * @param surfaceId
     * @param container
     * @param vertices
     * @param renderParams
     * @param view
     */
    function addSurface(toolkitComponent, toolkit, surfaceId, container, vertices, renderParams, view) {
      const templateRenderer = {
        reactive: true,
        asynchronous: true,
        update: (el, data, v, renderer) => {},
        render: (templateId, data, toolkit, objectType, surface, def, modelObject, node, eventInfo) => {
          if (def.component) {
            const el = document.createElement('div');
            if (def.component.mixins == null) {
              def.component.mixins = [];
            }
            const mixinRequired = browserUi.isGroup(modelObject) ? BaseGroupComponent : BaseNodeComponent;
            const hasNodeComponentMixin = def.component.mixins.find(m => m === mixinRequired);
            if (!hasNodeComponentMixin) {
              def.component.mixins.push(mixinRequired);
            }
            vertexWillRender(modelObject);

            // Here we do a similar thing to what happens in the React integration: we push a component definition and its props onto a reactive
            // array, and in the render function we use Teleport to create the component as a child of the DOM element we've created (in react we use
            // ReactDOM.createPortal to do a similar thing).

            const props = {
              obj: data,
              toolkit: vue.markRaw(toolkit),
              surface: vue.markRaw(surface),
              vertex: modelObject,
              el,
              key: modelObject.getFullId(),
              def: vue.markRaw(def),
              eventInfo: eventInfo == null ? null : vue.markRaw(eventInfo)
            };
            if (def.inject) {
              for (let i in def.inject) {
                const injection = def.inject[i];
                const injectedValue = typeof injection === 'function' ? injection(modelObject, toolkit) : injection;
                props[i] = injectedValue;
              }
            }
            vertices.push({
              component: vue.markRaw(def.component),
              el,
              key: modelObject.getFullId(),
              props
            });
          }
        },
        cleanupVertex: function (objId, el) {
          el._ref && el._ref.$destroy();
          el.parentNode && el.parentNode.removeChild(el);
        },
        cleanupPort: function (objId, el) {
          el._ref && el._ref.$destroy();
          el.parentNode && el.parentNode.removeChild(el);
        }
      };
      let rp = browserUi.extend(renderParams || {}, {
        view: view || {}
      });
      let surface = browserUi.render(toolkit, container, templateRenderer, rp);
      _finalizeSurface(surfaceId, surface);
      return surface;
    }

    /**
     * Finishes the registration of a surface, by setting its _vueId and flushing its work queue.
     * @param surfaceId
     * @param surface
     * @internal
     */
    function _finalizeSurface(surfaceId, surface) {
      surface._vueId = surfaceId;
      _surfaces[surfaceId] = surface;
      if (_workQueues[surfaceId]) {
        for (let i = 0; i < _workQueues[surfaceId].length; i++) {
          try {
            _workQueues[surfaceId][i][1](surface, _workQueues[surfaceId][i][0]);
          } catch (e) {
            browserUi.log("Cannot create Vue2 component " + e);
          }
        }
      }
      delete _workQueues[surfaceId];
    }

    /**
     * Register an externally created Surface with the Toolkit's Vue subsystem. This method lets you register a Surface that
     * you created using vanilla JS (for instance if you dont want to render using Vue components) and then other Vue components
     * such as a Palette or Inspector will be able to interact with it.
     * @param surfaceId
     * @param surface
     * @public
     */
    function registerSurface(surfaceId, surface) {
      _finalizeSurface(surfaceId, surface);
    }

    /**
     * Loads the Surface with the given ID.
     * @param surfaceId - ID of the Surface to retrieve
     * @param callback - Callback function to hit once the Surface has been retrieved.
     * @public
     */
    function loadSurface(surfaceId, callback) {
      if (_surfaces[surfaceId]) {
        callback(_surfaces[surfaceId]);
      } else {
        _addToWorkQueue(surfaceId, null, callback);
      }
    }

    /**
     * Add a component to the Surface with the given id. If the Surface already exists and has been initialised the component
     * will be added immediately; otherwise it will be enqueued for later processing. This is for internal use.
     * @param surfaceId - ID of the Surface to add the component to.
     * @param params - Constructor parameters for the component.
     * @param type - Type of component to add.
     * @internal
     */
    function addComponent(surfaceId, params, type) {
      _addToWorkQueue(surfaceId, params, _handlers[type]);
    }

    /**
     * Add a Palette to the Surface with the given id. If the Surface already exists and has been initialised the Palette
     * will be added immediately; otherwise it will be enqueued for later processing. This is really just a wrapper around
     * addComponent, and is for internal use also.
     * @param surfaceId - ID of the Surface to add the Palette to.
     * @param params - Constructor parameters for the Palette.
     * @internal
     */
    function addPalette(surfaceId, params) {
      addComponent(surfaceId, params, "palette");
    }

    /**
     * Add a Miniview to the Surface with the given id. If the Surface already exists and has been initialised the Miniview
     * will be added immediately; otherwise it will be enqueued for later processing. This is just a wrapper around addComponent.
     * @param surfaceId - ID of the Surface to add the Miniview to.
     * @param params - Constructor parameters for the Miniview.
     * @internal
     */
    function addMiniview(surfaceId, params) {
      addComponent(surfaceId, params, browserUi.MiniviewPlugin.type);
    }

    /**
     * Provides a mixin you can use to create a component that controls a set of droppable nodes for a Surface. See documentation for details.
     * @param surfaceId - ID of the Surface to configure the palette to drop to.
     * @param selector - CSS 3 selector identifying draggable child elements.
     * @public
     */
    const Palette = {
      props: {
        "surfaceId": String,
        "selector": String
      },
      mounted: function () {
        let self = this;
        addPalette(this.surfaceId, {
          element: self.$el.nodeType === 8 ? self.$el.parentNode : self.$el,
          selector: self.selector,
          dataGenerator: self.dataGenerator,
          typeExtractor: self.typeExtractor
        });
      }
    };

    /**
     * Base class for node/group components.
     * @public
     */
    const BaseVertexComponent = {
      props: {
        obj: Object,
        toolkit: BrowserUIVue3,
        vertex: browserUi.Vertex,
        surface: browserUi.Surface,
        el: Element,
        def: Object,
        eventInfo: Object
      },
      mounted() {
        this.surface.vertexRendered(this.vertex, this.el, this.def, this.eventInfo);
        vertexHasRendered(this.vertex);
      },
      methods: {
        /**
         * get the underlying Toolkit instance.
         * @public
         */
        getToolkit: function () {
          return this.toolkit;
        },
        /**
         * Removed the vertex this component representes.
         * @public
         */
        removeVertex: function () {
          this.toolkit.remove(this.vertex);
        }
      },
      updated() {
        this.surface.jsplumb.revalidate(this.el);
        vertexHasUpdated(this.vertex);
      }
    };

    /* ---------------------- dev tools for Toolkit's vue3 integration. Helps with internal testing. */

    /**
     * A hook for subclasses to use to indicate that they've acknowledged a vertex render request, and will subsequently call
     * vertexRendered once there is an element drawn.
     * @param v
     * @internal
     */
    function vertexWillRender(v) {
      unrenderedVertices.set(v.id, v);
    }
    const unrenderedVertices = new Map();
    const EVENT_VERTICES_RENDERED = "vertices:rendered";
    const EVENT_VERTEX_UPDATED = "vertex:updated";
    function vertexHasRendered(v) {
      unrenderedVertices.delete(v.id);
      // if we've now rendered every vertex in the queue, fire an event
      if (unrenderedVertices.size === 0) {
        devTools.fire(EVENT_VERTICES_RENDERED);
      }
    }
    function vertexHasUpdated(v) {
      devTools.fire(EVENT_VERTEX_UPDATED, v);
    }
    const devTools = new browserUi.OptimisticEventGenerator();
    function bindToDevLifecycle(event, handler) {
      devTools.bind(event, handler);
    }

    /* ----------------------------------------------------- */

    /**
     * Provides a mixin you should use to create a component that renders a node. See documentation for details.
     * @public
     */
    const BaseNodeComponent = {
      mixins: [BaseVertexComponent],
      methods: {
        getNode: function () {
          return this.vertex;
        },
        removeNode: function () {
          this.toolkit.removeNode(this.getNode());
        },
        updateNode: function (data) {
          this.toolkit.updateNode(this.vertex, data);
          const el = this.surface.getRenderedElement(this.vertex.getFullId());
          this.surface.jsplumb.revalidate(el);
        }
      }
    };

    /**
     * Provides a mixin you should use to create a component that renders a group. See documentation for details.
     * @public
     */
    const BaseGroupComponent = {
      methods: {
        /**
         * Gets the underlying group from the model.
         */
        getGroup: function () {
          return this.vertex;
        },
        /**
         * Removes the group this component represents from the model.
         * @param removeChildNodes
         */
        removeGroup: function (removeChildNodes) {
          this.toolkit.removeGroup(this.vertex, removeChildNodes);
        },
        /**
         * Updates the underlying group in the model.
         * @param data
         */
        updateGroup: function (data) {
          this.toolkit.updateGroup(this.vertex, data);
          const el = this.surface.getRenderedElement(this.vertex.getFullId());
          this.surface.jsplumb.revalidate(el);
        }
      },
      mixins: [BaseVertexComponent]
    };

    /**
     * The Toolkit's Vue3 integration plugin. See documentation for details.
     * @public
     */
    const JsPlumbToolkitVue3Plugin = {
      install: function (vue$1, options) {
        /**
         * This component provides a surface widget and an underlying toolkit instance.
         * @param renderParams - Parameters to configure the underlying surface, conforming to the `SurfaceRenderOptions` interface.
         * @param view - Mapping of model object types to components and behaviour, conforming to the `SurfaceViewOptions` interface.
         * @param surfaceId - ID to attach to the underlying surface. Required.
         * @param url - Optional url for a dataset to load.
         * @param id - ID of the underlying Toolkit. Required. Can be the same as `surfaceId`.
         * @public
         */
        vue$1.component(COMPONENT_TOOLKIT, {
          name: COMPONENT_TOOLKIT,
          props: [PROP_DATA, PROP_RENDER_PARAMS, PROP_TOOLKIT_PARAMS, PROP_VIEW, PROP_URL, PROP_ID, PROP_SURFACE_ID],
          data: function () {
            return {
              vertices: []
            };
          },
          mounted() {
            let self = this;
            let toolkit = addToolkit(self.id, self.toolkitParams);
            let container = this.$refs.root;
            if (self.url) {
              toolkit.load({
                url: self.url
              });
            } else if (self.data) {
              toolkit.load({
                data: self.data
              });
            }
            this.toolkit = toolkit;
            this.surface = addSurface(self, toolkit, self.surfaceId, container, this.vertices, self.renderParams, self.view);

            // These methods deal in updating/removing vertices.  The surface component is rendering an array of vertices, so whenever a vertex is removed, we
            // want to remove it from the list that surface is managing. When a vertex is updated, we want to update the data inside the vertices array, because
            // that's Vue's proxy, and its the one that, when changed, will cause the UI to update.

            const updateVertex = (vertexId, eventPayload) => {
              let mergeData = () => {
                let data = {};
                if (eventPayload.originalData || eventPayload.updates) {
                  data = browserUi.extend(eventPayload.originalData, eventPayload.updates);
                } else if (eventPayload.newData) {
                  browserUi.extend(data, eventPayload.newData);
                }
                return data;
              };
              const entry = this.vertices.find(v => v.key === vertexId);
              if (entry != null) {
                entry.props.obj = mergeData();
              }
            };
            const removeVertex = id => {
              const idx = browserUi.findWithFunction(this.vertices, n => n.key === id);
              if (idx !== -1) {
                this.vertices.splice(idx, 1);
              }
            };
            this.toolkit.bind(browserUi.EVENT_NODE_UPDATED, p => {
              updateVertex(p.vertex.getFullId(), p);
            });
            this.toolkit.bind(browserUi.EVENT_GROUP_UPDATED, p => {
              updateVertex(p.vertex.getFullId(), p);
            });
            this.toolkit.bind(browserUi.EVENT_NODE_REMOVED, p => {
              removeVertex(p.node.id);
            });
            this.toolkit.bind(browserUi.EVENT_GROUP_REMOVED, p => {
              removeVertex(p.group.id);
            });
          },
          render: function () {
            //
            // render the component container, and then for each vertex in the vertices array, render it, with the supplied props, using
            // a Teleporter to put it inside the DOM element we created. note the use of 'key' here; without it, components are re-rendered whenever
            // there is a delta.
            //
            return vue.h('div', {
              ref: 'root',
              style: {
                "width": "100%",
                "height": "100%"
              }
            }, this.vertices.map(v => {
              return vue.h(vue.Teleport, {
                to: v.el,
                key: v.key
              }, [vue.h(v.component, v.props)]);
            }));
          }
        });

        /**
         * This component provides a miniview widget.
         * @param surfaceId - The ID of the surface to which to attach the miniview. Required.
         * @param className - Optional CSS to add to the container element for the miniview.
         * @public
         */
        vue$1.component(COMPONENT_MINIVIEW, {
          name: COMPONENT_MINIVIEW,
          props: [PROP_SURFACE_ID, PROP_CLASS_NAME, PROP_ACTIVE_TRACKING, PROP_CLICK_TO_CENTER],
          mounted: function () {
            const self = this;
            addMiniview(self.surfaceId, {
              container: self.$el,
              activeTracking: self.PROP_ACTIVE_TRACKING,
              clickToCenter: self.PROP_CLICK_TO_CENTER
            });
          },
          render: function (_ctx) {
            return vue.h('div', {
              ref: 'root',
              class: _ctx.className
            });
          }
        });
      }
    };
    const PROP_CLICK_TO_CENTER = "clickToCenter";
    const PROP_ACTIVE_TRACKING = "activeTracking";
    const PROP_SURFACE_ID = "surfaceId";
    const PROP_CLASS_NAME = "className";
    const PROP_DATA = "data";
    const PROP_RENDER_PARAMS = "renderParams";
    const PROP_TOOLKIT_PARAMS = "toolkitParams";
    const PROP_VIEW = "view";
    const PROP_URL = "url";
    const PROP_ID = "id";
    const COMPONENT_MINIVIEW = 'jsplumb-miniview';
    const COMPONENT_TOOLKIT = "jsplumb-toolkit";

    /**
     * Component to assist in adding drag/drop of new nodes/groups. This uses the low level DragManager under the hood - for most
     * applications you probably want the SurfaceDrop component instead.
     * @public
     */
    const DragDrop = {
      props: ["surfaceId", "selector", "dataGenerator"],
      mounted: function () {
        let component = this;
        loadSurface(this.surfaceId, surface => {
          // docs for this are here https://docs.jsplumbtoolkit.com/toolkit/current/articles/drop-manager.html#controlling-drop

          let params = {
            source: component.$el,
            selector: component.selector,
            surface: surface,
            dataGenerator: el => {
              if (!component.dataGenerator) {
                return {
                  type: "default"
                };
              } else {
                return component.dataGenerator(el);
              }
            }
          };
          if (component.onEdgeDrop) {
            params.onEdgeDrop = (data, edge, el, evt, pageLocation, canvasLocation) => {
              let positionOnSurface = surface.fromPageLocation.apply(surface, pageLocation);
              component.onEdgeDrop(surface, data, edge, positionOnSurface, el, evt, pageLocation);
            };
          }
          if (component.onCanvasDrop) {
            params.onCanvasDrop = (data, canvasPosition, draggedElement, e, position) => {
              component.onCanvasDrop(surface, data, position);
            };
          }
          if (component.onDrop) {
            params.onDrop = (data, target, draggedElement, e, position) => {
              component.onDrop(surface, data, target, position, draggedElement, e);
            };
          }
          this.dropManager = new browserUi.DropManager(params);
        });
      }
    };

    /**
     * Component to assist in adding drag/drop of new nodes/groups. This uses the SurfaceDropManager under the hood - for most
     * applications this will be sufficient. If you need finer-grained control, check out the DragDrop component.
     * @public
     */
    const SurfaceDrop = {
      props: ["surfaceId", "selector", "dataGenerator", "typeDescriptor", "groupIdentifier", "allowDropOnEdge", "allowDropOnCanvas", "allowDropOnGroup"],
      mounted: function () {
        let component = this;

        //let _addSurface = function.bind(this);

        loadSurface(this.surfaceId, surface => {
          // docs for this are here https://docs.jsplumbtoolkit.com/toolkit/current/articles/drop-manager.html#controlling-drop

          let params = {
            source: component.$el,
            selector: component.selector,
            surface: surface,
            dataGenerator: el => {
              if (!component.dataGenerator) {
                return {
                  type: "default"
                };
              } else {
                return component.dataGenerator(el);
              }
            },
            allowDropOnEdge: component.allowDropOnEdge !== "false",
            allowDropOnGroup: component.allowDropOnGroup !== "false",
            allowDropOnCanvas: component.allowDropOnCanvas !== "false"
          };
          if (component.groupIdentifier != null) {
            params.groupIdentifier = component.groupIdentifier;
          }
          if (component.typeGenerator != null) {
            params.typeGenerator = component.typeGenerator;
          }
          this.dropManager = new browserUi.SurfaceDropManager(params);
        });
      }
    };

    const TAG_SHAPE = "jsplumb-shape";
    const TAG_SHAPE_PALETTE = "jsplumb-shape-palette";

    /**
     * Shape library plugin for Vue 3. See documentation for details.
     * @public
     */
    const ShapeLibraryPlugin = {
      install: function (vue$1, options) {
        /**
         * A vue component that renders a shape from a shape library.
         * @param shapeLibrary The shape library to extract shape data from
         * @param obj The data representing the object to render. Its `type` member will be used to lookup the SVG.
         * @param strokeWidth Optional stroke width to use when painting the shape. Defaults to 2.
         */
        vue$1.component(TAG_SHAPE, {
          name: TAG_SHAPE,
          props: ["shapeLibrary", "obj", "showLabels", "labelProperty", "labelStrokeWidth"],
          mounted() {
            const el = this.shapeLibrary.renderCompiledShape(Object.assign({
              sw: this.obj.outlineWidth || 2
            }, this.obj));
            this.$refs.container.appendChild(el);
            if (this.showLabels) {
              const lbl = this.shape.renderShapeLabel(this.obj, this.labelProperty, this.labelStrokeWidth);
              this.$refs.container.appendChild(lbl);
            }
          },
          updated() {
            const el = this.shapeLibrary.renderCompiledShape(Object.assign({
              sw: this.obj.outlineWidth || 2
            }, this.obj));
            this.$refs.container.replaceChildren(el);
            if (this.showLabels) {
              const lbl = this.shape.renderShapeLabel(this.obj, this.labelProperty, this.labelStrokeWidth);
              this.$refs.container.appendChild(lbl);
            }
          },
          render: function () {
            return vue.h("svg", {
              ref: "container",
              width: "100%",
              height: "100%",
              preserveAspectRatio: "none",
              fill: this.obj.fill,
              stroke: this.obj.outline,
              viewBox: '0 0 ' + this.obj.width + ' ' + this.obj.height,
              "stroke-width": this.obj.outlineWidth || 2
            });
          }
        });

        /**
         * Shape library palette element.
         * @param shapeLibrary The shape library to render. Required.
         * @param surfaceId The ID of the surface to attach to. Required.
         * @param dragSize Optional size to use for dragged elements.
         * @param dragSize Optional size to use for icons.
         * @param fill Optional fill color to use for dragged elements. This should be in RGB format, _not_ a color like 'white' or 'cyan' etc.
         * @param outline Optional color to use for outline of dragged elements. Should be in RGB format.
         * @param showAllMessage Message to use for the 'show all' option in the shape set drop down when there is more than one set of shapes. Defaults to `Show all`.
         * @param selectAfterDrop When true (which is the default), a newly dropped vertex will be set as the underlying Toolkit's selection.
         * @param canvasStrokeWidth Stroke width to use for shapes dropped on canvas. Defaults to 2.
         * @param paletteStrokeWidth Stroke width to use for shapes in palette. Defaults to 1.
         * @param dataGenerator Optional function you can use to set some initial data for an element being dragged. Note that you cannot
         * override the object's `type` with this function.
         */
        vue$1.component(TAG_SHAPE_PALETTE, {
          name: TAG_SHAPE_PALETTE,
          props: {
            shapeLibrary: browserUi.ShapeLibraryImpl,
            surfaceId: String,
            dragSize: Object,
            iconSize: Object,
            fill: String,
            outline: String,
            showAllMessage: String,
            selectAfterDrop: Boolean,
            canvasStrokeWidth: Number,
            paletteStrokeWidth: Number,
            dataGenerator: Function,
            initialSet: String
          },
          mounted() {
            loadSurface(this.surfaceId, surface => {
              new browserUi.ShapeLibraryPalette({
                container: this.$refs.container,
                surface,
                shapeLibrary: this.shapeLibrary,
                dragSize: this.dragSize,
                iconSize: this.iconSize,
                fill: this.fill,
                outline: this.outline,
                showAllMessage: this.showAllMessage,
                selectAfterDrop: this.selectAfterDrop,
                canvasStrokeWidth: this.canvasStrokeWidth,
                paletteStrokeWidth: this.paletteStrokeWidth,
                dataGenerator: this.dataGenerator,
                initialSet: this.initialSet
              });
            });
          },
          render: function () {
            return vue.h("div", {
              ref: "container"
            });
          }
        });
      }
    };

    const TAG_EDGE_TYPE_PICKER = "jsplumb-edge-type-picker";

    /**
     * Utilities for working with inspectors. This plugin contains a single
     * component - `jsplumb-edge-type-picker` - that you can use to embed an edge type picker in your UI.
     * @public
     */
    const InspectorPlugin = {
      install: function (vue$1, options) {
        /**
         * A vue component that renders an edge type picker
         * @param edgeMappings Edge mappings to render and allow selection from
         * @param inspector Inspector to get/set values from/to
         * @param propertyName Name of the property to set in the edge data
         */
        vue$1.component(TAG_EDGE_TYPE_PICKER, {
          name: TAG_EDGE_TYPE_PICKER,
          props: {
            edgeMappings: Object,
            inspector: Object,
            propertyName: String
          },
          mounted() {
            const e = new browserUi.EdgeTypePicker(this.inspector.toolkit, this.$refs.container, this.edgeMappings, this.inspector.getValue(this.propertyName), v => {
              this.inspector.setValue(this.propertyName, v);
            });
            e.render(this.propertyName);
            this.inspector.onChange(() => {
              e.select(this.inspector.getValue(this.propertyName));
            });
          },
          render: function () {
            return vue.h("div", {
              ref: "container"
            });
          }
        });
      }
    };

    exports.BaseGroupComponent = BaseGroupComponent;
    exports.BaseNodeComponent = BaseNodeComponent;
    exports.BrowserUIVue3 = BrowserUIVue3;
    exports.COMPONENT_MINIVIEW = COMPONENT_MINIVIEW;
    exports.COMPONENT_TOOLKIT = COMPONENT_TOOLKIT;
    exports.DragDrop = DragDrop;
    exports.EVENT_VERTEX_UPDATED = EVENT_VERTEX_UPDATED;
    exports.EVENT_VERTICES_RENDERED = EVENT_VERTICES_RENDERED;
    exports.InspectorPlugin = InspectorPlugin;
    exports.JsPlumbToolkitVue3Plugin = JsPlumbToolkitVue3Plugin;
    exports.PROP_ACTIVE_TRACKING = PROP_ACTIVE_TRACKING;
    exports.PROP_CLASS_NAME = PROP_CLASS_NAME;
    exports.PROP_CLICK_TO_CENTER = PROP_CLICK_TO_CENTER;
    exports.PROP_DATA = PROP_DATA;
    exports.PROP_ID = PROP_ID;
    exports.PROP_RENDER_PARAMS = PROP_RENDER_PARAMS;
    exports.PROP_SURFACE_ID = PROP_SURFACE_ID;
    exports.PROP_TOOLKIT_PARAMS = PROP_TOOLKIT_PARAMS;
    exports.PROP_URL = PROP_URL;
    exports.PROP_VIEW = PROP_VIEW;
    exports.Palette = Palette;
    exports.ShapeLibraryPlugin = ShapeLibraryPlugin;
    exports.SurfaceDrop = SurfaceDrop;
    exports.TAG_EDGE_TYPE_PICKER = TAG_EDGE_TYPE_PICKER;
    exports.TAG_SHAPE = TAG_SHAPE;
    exports.TAG_SHAPE_PALETTE = TAG_SHAPE_PALETTE;
    exports.addComponent = addComponent;
    exports.addMiniview = addMiniview;
    exports.addPalette = addPalette;
    exports.addSurface = addSurface;
    exports.addToolkit = addToolkit;
    exports.bindToDevLifecycle = bindToDevLifecycle;
    exports.loadSurface = loadSurface;
    exports.newInstance = newInstance;
    exports.registerSurface = registerSurface;

    Object.defineProperty(exports, '__esModule', { value: true });

}));
